import type { HTMLAttributes } from 'react';
import { clsx } from 'clsx';

/** Structural metadata, compatible with the released DTO; no fetching, bodies or permission inference. */
export interface SkillInventoryItem {
  name: string; description?: string | null; version?: string | null; pin?: string | null;
  source: string; engines?: readonly string[]; availability?: string;
}
export interface AgentSkillInventoryRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  skill: SkillInventoryItem; locale: 'en' | 'zh-Hans'; showsAvailability?: boolean;
}

// app/0.7.0 HyloUI/Resources/Localizable.xcstrings. Names/descriptions/pins remain literal.
const copy = {
  en: { installed: 'Installed with Hylo', registry: 'Skill registry', sourceUnknown: 'Skill source unknown',
    available: 'Available', unavailable: 'Unavailable', unknown: 'Unknown' },
  'zh-Hans': { installed: '随 Hylo 安装', registry: '技能库', sourceUnknown: '技能来源未知',
    available: '可用', unavailable: '不可用', unknown: '未知' },
};

export function AgentSkillInventoryRow({ skill, locale, showsAvailability = true, className, ...props }: AgentSkillInventoryRowProps) {
  const t = copy[locale] ?? copy.en;
  const source = skill.source === 'hylo_installer' ? t.installed : skill.source === 'claw_registry' ? t.registry : t.sourceUnknown;
  const engines = (skill.engines ?? []).map(engine => engine === 'claude' ? 'Claude' : engine === 'codex' ? 'Codex' : engine).join(', ');
  const metadata = engines ? `${source} · ${engines}` : source;
  const availability = skill.availability === 'available' ? t.available : skill.availability === 'unavailable' ? t.unavailable : t.unknown;
  const showAvailability = showsAvailability && skill.source !== 'hylo_installer';
  const accessibleText = [skill.name, skill.version, skill.description, metadata, showAvailability ? availability : null]
    .filter(value => value !== null && value !== undefined).join(', ');
  return <div {...props} role="group" aria-label={accessibleText} className={clsx('hylo-skill-row', className)}>
    <div aria-hidden="true" className="hylo-skill-row__content">
      <div className="hylo-skill-row__heading"><strong>{skill.name}</strong>
        {skill.version != null && <span className="hylo-skill-row__version">{skill.version}</span>}
        {showAvailability && <span className="hylo-skill-row__availability">{availability}</span>}
      </div>
      {skill.description != null && <span className="hylo-skill-row__description">{skill.description}</span>}
      <span className="hylo-skill-row__metadata"><span className="hylo-skill-row__source">{source}</span>{engines && ` · ${engines}`}</span>
      {skill.pin != null && <code className="hylo-skill-row__pin">{skill.pin}</code>}
    </div>
  </div>;
}
